import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, ChevronDown, ChevronRight, Check, Edit, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FeeGroupFormModal } from "./fee-group-form-modal";
import { FeeFormModal } from "./fee-form-modal";
import type { LenderFeeGroup, LenderFee } from "@shared/schema";

interface FeesPanelProps {
  lenderId: string;
}

export function FeesPanel({ lenderId }: FeesPanelProps) {
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<LenderFeeGroup | undefined>();
  const [selectedFee, setSelectedFee] = useState<LenderFee | undefined>();
  const [activeFeeGroupId, setActiveFeeGroupId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
  const [deleteFeeId, setDeleteFeeId] = useState<string | null>(null);

  console.log("[ISSUE-072 DEBUG] FeesPanel rendered with lenderId:", lenderId);

  const { toast } = useToast();

  const { data: feeGroups = [], isLoading: isLoadingGroups } = useQuery<LenderFeeGroup[]>({
    queryKey: ["/api/crm/lenders", lenderId, "fee-groups"],
  });

  const { data: fees = [], isLoading: isLoadingFees } = useQuery<LenderFee[]>({
    queryKey: ["/api/crm/fee-groups", activeFeeGroupId, "fees"],
    enabled: !!activeFeeGroupId,
  });

  // Delete fee group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log("[ISSUE-072 DEBUG] Delete mutation starting for fee group:", id);
      await apiRequest("DELETE", `/api/crm/fee-groups/${id}`);
      console.log("[ISSUE-072 DEBUG] DELETE request completed");
    },
    onSuccess: () => {
      console.log("[ISSUE-072 DEBUG] onSuccess started, lenderId:", lenderId);
      // Use predicate to match ALL lender-related queries (fixes ISSUE-072)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((key) =>
            typeof key === "string" && key.includes("/api/crm/lenders")
          ),
      });
      console.log("[ISSUE-072 DEBUG] Invalidation completed");
      toast({ title: "Fee group deleted", description: "The fee group and all its fees have been deleted." });
      setDeleteGroupId(null);
      console.log("[ISSUE-072 DEBUG] onSuccess completed");
    },
    onError: (error: Error) => {
      console.error("[ISSUE-072 DEBUG] onError:", error);
      toast({ title: "Error", description: error.message || "Failed to delete fee group", variant: "destructive" });
    },
  });

  // Delete fee mutation
  const deleteFeeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/lender-fees/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/fee-groups", activeFeeGroupId, "fees"] });
      toast({ title: "Fee deleted", description: "The fee has been deleted." });
      setDeleteFeeId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete fee", variant: "destructive" });
    },
  });

  const toggleGroupExpansion = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
      // Clear active fee group if we're collapsing it
      if (activeFeeGroupId === groupId) {
        setActiveFeeGroupId(null);
      }
    } else {
      newExpanded.add(groupId);
      // Set active fee group to fetch fees
      setActiveFeeGroupId(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleEditGroup = (group: LenderFeeGroup, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion when editing
    setSelectedGroup(group);
    setShowGroupForm(true);
  };

  const handleEditFee = (fee: LenderFee) => {
    setSelectedFee(fee);
    setShowFeeForm(true);
  };

  const handleAddFee = (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion when adding fee
    setActiveFeeGroupId(groupId);
    setShowFeeForm(true);
  };

  const handleCloseFeeGroupModal = () => {
    setShowGroupForm(false);
    setSelectedGroup(undefined);
  };

  const handleCloseFeeModal = () => {
    setShowFeeForm(false);
    setSelectedFee(undefined);
  };

  if (isLoadingGroups) {
    return <div className="p-4">Loading fee groups...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Fee Groups</h3>
        <Button
          size="sm"
          onClick={() => setShowGroupForm(true)}
          data-testid="button-add-fee-group"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Fee Group
        </Button>
      </div>

      {feeGroups.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            No fee groups found. Click "Add Fee Group" to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {feeGroups.map((group) => {
            const isExpanded = expandedGroups.has(group.id);
            const groupFees = activeFeeGroupId === group.id ? fees : [];

            return (
              <Card key={group.id}>
                <CardHeader
                  className="pb-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => toggleGroupExpansion(group.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 flex items-start gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <CardTitle
                            className="text-base hover:text-primary"
                            onClick={(e) => handleEditGroup(group, e)}
                          >
                            {group.groupName}
                          </CardTitle>
                          {group.isDefault && <Badge variant="default">Default</Badge>}
                        </div>
                        {group.description && (
                          <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isExpanded && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => handleAddFee(group.id, e)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Fee
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleEditGroup(group, e)}
                        data-testid={`button-edit-fee-group-${group.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteGroupId(group.id);
                        }}
                        data-testid={`button-delete-fee-group-${group.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="pt-0">
                    {isLoadingFees ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        Loading fees...
                      </div>
                    ) : groupFees.length === 0 ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        No fees in this group. Click "Add Fee" to create one.
                      </div>
                    ) : (
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow className="h-9 bg-muted/50">
                              <TableHead className="text-xs font-medium px-3 py-1.5">Fee Name</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5">Type</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5">Frequency</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5 text-right">Amount</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5 text-center w-[80px]">Mandatory</TableHead>
                              <TableHead className="text-xs font-medium px-3 py-1.5 w-[80px]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {groupFees.map((fee) => (
                              <TableRow
                                key={fee.id}
                                className="h-10 cursor-pointer hover:bg-accent/50 transition-colors"
                                data-testid={`row-fee-${fee.id}`}
                              >
                                <TableCell className="px-3 py-1.5">
                                  <span className="font-medium text-sm">{fee.feeName}</span>
                                </TableCell>
                                <TableCell className="px-3 py-1.5">
                                  <span className="text-sm text-muted-foreground capitalize">
                                    {fee.feeType?.replace(/_/g, " ") || "-"}
                                  </span>
                                </TableCell>
                                <TableCell className="px-3 py-1.5">
                                  <span className="text-sm text-muted-foreground capitalize">
                                    {fee.feeFrequency && fee.feeFrequency !== "one_time"
                                      ? fee.feeFrequency.replace(/_/g, " ")
                                      : "-"}
                                  </span>
                                </TableCell>
                                <TableCell className="px-3 py-1.5 text-right">
                                  <span className="text-sm font-medium tabular-nums">
                                    {fee.amountType === "fixed"
                                      ? `$${parseFloat(fee.fixedAmount || "0").toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : `${(parseFloat(fee.percentageAmount || "0") * 100).toFixed(2)}%`}
                                  </span>
                                </TableCell>
                                <TableCell className="px-3 py-1.5 text-center">
                                  {fee.isMandatory ? (
                                    <Badge variant="default" className="text-xs">
                                      <Check className="h-3 w-3 mr-1" />
                                      Yes
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">No</span>
                                  )}
                                </TableCell>
                                <TableCell className="px-3 py-1.5">
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditFee(fee);
                                      }}
                                      data-testid={`button-edit-fee-${fee.id}`}
                                    >
                                      <Edit className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteFeeId(fee.id);
                                      }}
                                      data-testid={`button-delete-fee-${fee.id}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <FeeGroupFormModal
        open={showGroupForm}
        onOpenChange={handleCloseFeeGroupModal}
        lenderId={lenderId}
        feeGroup={selectedGroup}
      />

      {activeFeeGroupId && (
        <FeeFormModal
          open={showFeeForm}
          onOpenChange={handleCloseFeeModal}
          feeGroupId={activeFeeGroupId}
          fee={selectedFee}
        />
      )}

      {/* Delete Fee Group Confirmation Dialog */}
      <Dialog open={!!deleteGroupId} onOpenChange={(open) => !open && setDeleteGroupId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Fee Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this fee group? All fees within this group will also be deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteGroupId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                console.log("[ISSUE-072 DEBUG] Delete button clicked, deleteGroupId:", deleteGroupId);
                console.log("[ISSUE-072 DEBUG] Calling mutation.mutate() unconditionally...");
                deleteGroupMutation.mutate(deleteGroupId!);
              }}
              disabled={!deleteGroupId || deleteGroupMutation.isPending}
              data-testid="button-confirm-delete-fee-group"
            >
              {deleteGroupMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Fee Confirmation Dialog */}
      <Dialog open={!!deleteFeeId} onOpenChange={(open) => !open && setDeleteFeeId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Fee</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this fee? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFeeId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteFeeId && deleteFeeMutation.mutate(deleteFeeId)}
              disabled={deleteFeeMutation.isPending}
              data-testid="button-confirm-delete-fee"
            >
              {deleteFeeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
